import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import type { UnresolvedRef } from "@/lib/coach-manager/transform";
import type { Contract } from "@/lib/coach-manager/types";
import { copyOperationKeys } from "@/lib/query";
import type {
  CreateCopyOperationRequest,
  PatchCopyOperationStagingInput,
} from "@/schemas/copy-operations";

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/** A `copy_operation` row as returned by the API — mirrors the Drizzle select shape. */
export interface CopyOperation {
  id: string;
  objectType: string;
  sourceBuId: string;
  sourceObjectId: string;
  targetBuId: string;
  targetCustomerId: string;
  status: string;
  initiatedBy: string;
  initiatedAt: string;
  completedAt: string | null;
  failureReason: string | null;
  sourceSnapshot: Contract;
  stagedPayload: Contract;
  unresolvedRefs: UnresolvedRef[];
  resultObjectId: string | null;
}

/** A `contract_movement` row from `copy_operation_entity` — the source→target Movement ID map. */
export interface CopyOperationMovementMapping {
  sourceEntityId: string;
  targetEntityId: string | null;
}

export interface CopyOperationDetail {
  operation: CopyOperation;
  sourceBuName: string | null;
  targetBuName: string | null;
  movementMappings: CopyOperationMovementMapping[];
}

/** One row of the Copy Operations Log list — the flattened, joined shape `queryCopyOperations` returns. */
export interface CopyOperationListRow {
  id: string;
  objectType: string;
  sourceBuId: string;
  sourceBuName: string | null;
  sourceObjectId: string;
  targetBuId: string;
  targetBuName: string | null;
  targetCustomerId: string;
  status: string;
  resultObjectId: string | null;
  movementCount: number;
  initiatedBy: string;
  initiatedByName: string | null;
  initiatedByEmail: string | null;
  initiatedAt: string;
  completedAt: string | null;
  failureReason: string | null;
}

interface CopyOperationsListResponse {
  data: CopyOperationListRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export type CreateCopyOperationResult =
  | { ok: true; data: CopyOperation }
  | { ok: false; error: string };

/**
 * "Initiate Copy": create a staging draft (transform runs server-side, a
 * `copy_operation` row is persisted immediately). Never throws on a domain
 * failure (`{ ok: false, error }` — e.g. no Company ID on the target BU) —
 * only on a transport/validation failure of the route itself.
 */
export function useCreateCopyOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCopyOperationRequest): Promise<CreateCopyOperationResult> => {
      const res = await fetch("/api/copy-operations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (res.status === 201) {
        const json = await res.json();
        return { ok: true, data: json.data };
      }
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to start copy"));
      return res.json();
    },
    onSuccess: (result) => {
      if (result.ok) queryClient.invalidateQueries({ queryKey: copyOperationKeys.all });
    },
  });
}

/** One `copy_operation` row for the staging editor / detail page. */
export function useCopyOperation(id: string | null) {
  return useQuery({
    queryKey: copyOperationKeys.detail(id ?? ""),
    queryFn: async (): Promise<CopyOperationDetail> => {
      const res = await fetch(`/api/copy-operations/${id}`);
      if (!res.ok)
        throw new Error(await extractErrorMessage(res, "Failed to fetch copy operation"));
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(id),
  });
}

export type UpdateStagingResult = { ok: true; data: CopyOperation } | { ok: false; error: string };

/**
 * Staging autosave (`PATCH /api/copy-operations/:id`). Rejected (409) once
 * the operation is `in_progress`/`completed` — the caller renders that as an
 * error same as any other failure, since edits shouldn't be possible then.
 */
export function useUpdateCopyOperationStaging(id: string) {
  return useMutation({
    mutationFn: async (patch: PatchCopyOperationStagingInput): Promise<UpdateStagingResult> => {
      const res = await fetch(`/api/copy-operations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) return { ok: false, error: json?.error ?? "Failed to save changes" };
      return { ok: true, data: json.data };
    },
  });
}

export type CommitCopyOperationResult =
  | { ok: true; data: CopyOperation }
  | { ok: false; error: string };

/**
 * Post the staged Contract to the target BU (`POST /api/copy-operations/commit`).
 * Never throws on a domain failure (bad data rejected by Coach Manager) —
 * that's a normal `{ ok: false, error }` result the staging page renders
 * inline and lets the user fix + retry.
 */
export function useCommitCopyOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<CommitCopyOperationResult> => {
      const res = await fetch("/api/copy-operations/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const json = await res.json().catch(() => null);
      // A domain-level rejection (e.g. Coach Manager rejecting the data) is
      // returned with HTTP 200 and an explicit `ok: false` body, so it's not
      // mistaken for a transport error — check that before falling back to
      // `res.ok`, which only catches not-found/wrong-status/config failures.
      if (json?.ok === false) {
        return { ok: false, error: json.error ?? "Failed to post to target BU" };
      }
      if (!res.ok) return { ok: false, error: json?.error ?? "Failed to post to target BU" };
      return { ok: true, data: json.data };
    },
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: copyOperationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: copyOperationKeys.all });
    },
  });
}

/**
 * Delete a copy operation (`DELETE /api/copy-operations/:id`) — admin-only on
 * the backend, and only accepted while `pending`/`failed`. Cascades to its
 * `copy_operation_entity` rows at the DB level.
 */
export function useDeleteCopyOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/copy-operations/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Delete failed"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: copyOperationKeys.all });
      toast.success("Copy operation deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete copy operation");
    },
  });
}

/** The Copy Operations Log list — standard data-grid wiring. */
export function useCopyOperations(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...copyOperationKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<CopyOperationsListResponse> => {
      const res = await fetch(`/api/copy-operations?${buildListParams(query, pageRequest)}`);
      if (!res.ok)
        throw new Error(await extractErrorMessage(res, "Failed to fetch copy operations"));
      return res.json();
    },
  });
}

export interface PriorCopyOperationsParams {
  sourceBuId: string;
  sourceObjectId: string;
  targetBuId: string;
  targetCustomerId: string;
}

/** A prior `copy_operation` row as returned by the API — mirrors the Drizzle select shape. */
export interface PriorCopyOperation {
  id: string;
  status: string;
  initiatedAt: string;
  completedAt: string | null;
  resultObjectId: string | null;
}

/**
 * Idempotency-warning check (`GET /api/copy-operations/prior-attempts`) — has
 * this exact source→target pair been copied before? `params` is `null` until
 * the caller has a complete source+target selection; the query stays disabled
 * until then. Purely informational — callers must never gate a copy action
 * on this result, only surface it as a warning.
 */
export function usePriorCopyOperations(params: PriorCopyOperationsParams | null) {
  return useQuery({
    queryKey: copyOperationKeys.priorAttempts(params),
    queryFn: async (): Promise<PriorCopyOperation[]> => {
      if (!params) return [];
      const search = new URLSearchParams({
        sourceBuId: params.sourceBuId,
        sourceObjectId: params.sourceObjectId,
        targetBuId: params.targetBuId,
        targetCustomerId: params.targetCustomerId,
      });
      const res = await fetch(`/api/copy-operations/prior-attempts?${search}`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to check prior copies"));
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(params),
  });
}
